import React from 'react';
import Table, { cellTypes } from 'core/table';
import { TableViewSettingsToggle } from 'modules/catalog/tableViewSettingsToggle';
import * as f from 'utils/fieldTypes';
import { getItem, setItem } from 'utils/localStorage';
import { isArray, isObject } from 'utils/helpers';
import { ToolbarContainer, TableContainer } from './PreviewResultsTable.styled';
import { getValueCellForTable, getValueCell } from './utils';

// type Props = {
//   /** If data has been previewed. */
//   didPreviewData: boolean,
//   /** Object of all the keys in the catalog. */
//   fieldTypes: {},
//   /** If currently loading table. */
//   isLoading: boolean,
//   /** The min width of a table cell. */
//   minCellWidth: number,
//   /** The max width of a table cell. */
//   maxCellWidth: number,
//   /** The row data of the table. */
//   rows: [],
//   /** optional message */
//   headerContent?: React.ReactNode,
// };

const LOCAL_STORAGE_ID = 'catalogCollectionsPreviewTableMode';

export default class PreviewResultsTable extends React.Component {
  static defaultProps = {
    fieldTypes: [],
    minCellWidth: 200,
    maxCellWidth: 600,
    rows: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      isTableView: getItem(LOCAL_STORAGE_ID, false),
    };
  }

  /**
   * handleChangeViewMode
   * Callback function when view mode toggle is changed.
   * @param { Object } e: The change event.
   * @param { Object } updatedProps: Updated props containing the updated value of `isTableView`.
   * @returns { void }
   */
  handleChangeViewMode = (e, { isTableView }) => {
    setItem(LOCAL_STORAGE_ID, isTableView);
    this.setState({ isTableView });
  };

  /**
   * getValueCell
   * Renders the JSON Tree value cell.
   * @param { Object } rowProps: Props of the row containing the value.
   * @returns { React.Component } The rendered JSON Tree.
   */
  getValueCell = getValueCell;

  /**
   * getValueCellForTable
   * Renders the Table value cell.
   * @param { Object } rowProps: Props of the row containing the value.
   * @returns { React.Component } The rendered JSON Tree.
   */
  getValueCellForTable = getValueCellForTable;

  /**
   * getDataForJsonView
   * Gets the table data for JSON view.
   * @returns { Object } Object containing formatted columns and rows.
   */
  getDataForJsonView = () => {
    const { minCellWidth, rows } = this.props;
    const columns = [
      {
        name: 'Key',
        value: 'key',
        maxWidth: 300,
      },
      {
        name: 'Value',
        value: 'value',
        type: cellTypes.CUSTOM,
        minWidth: minCellWidth * 2,
        getCellContent: this.getValueCell,
      },
    ];
    return { columns, rows };
  };

  /**
   * getTableColumnForField
   * Determines the column for the field.
   * @param { String } name: The name of the field.
   * @param { Any } value: The value of the field.
   * @param { Number } index: The index of the column.
   * @returns { Object } Object containing formatted columns and rows.
   */
  getTableColumnForField = (name, value, index) => {
    const { minCellWidth, maxCellWidth } = this.props;
    const isArrayOrObject = isArray(value) || isObject(value);
    const minWidth = isArrayOrObject ? minCellWidth * 2 : minCellWidth;
    const maxWidth = isArrayOrObject ? maxCellWidth * 2 : maxCellWidth;
    const getCellContent = isArrayOrObject ? this.getValueCell : this.getValueCellForTable;
    return {
      name,
      value: name,
      type: cellTypes.CUSTOM,
      minWidth,
      maxWidth: index > 0 ? maxWidth : undefined,
      getCellContent,
    };
  };

  /**
   * getDataForTableView
   * Gets the table data for Table view.
   * @returns { Object } Object containing formatted columns and rows.
   */
  getDataForTableView = () => {
    const { fieldTypes, rows } = this.props;
    const allFieldTypes = { Key: f.STRING, ...fieldTypes };
    const columns = Object.keys(allFieldTypes).map((fieldName, index) =>
      this.getTableColumnForField(fieldName, fieldTypes[fieldName], index),
    );
    const formattedRows = rows.map(({ key, value }) => ({ Key: key, ...value }));
    return { columns, rows: formattedRows };
  };

  render() {
    const { isTableView } = this.state;
    const { didPreviewData, isLoading, headerContent } = this.props;

    if (didPreviewData) {
      const { columns, rows } = isTableView
        ? this.getDataForTableView()
        : this.getDataForJsonView();
      const total = rows.length;

      return (
        <TableContainer>
          <Table
            columns={columns}
            dockOnBottom
            dockLeftColumn
            hidePagination
            isLoading={isLoading}
            rows={rows}
            total={total}
            toolbarContent={
              <ToolbarContainer $hasHeaderContent={!!headerContent}>
                {headerContent}
                <TableViewSettingsToggle
                  onChange={this.handleChangeViewMode}
                  isTableView={isTableView}
                />
              </ToolbarContainer>
            }
          />
        </TableContainer>
      );
    }
    return <div />;
  }
}
